import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

export function SessionRecovery() {
  const [isRecovering, setIsRecovering] = useState(false);
  const lastActiveTime = useRef(Date.now());
  const hasBeenHidden = useRef(false);

  useEffect(() => {
    // Track when the page becomes hidden/visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hasBeenHidden.current = true;
        lastActiveTime.current = Date.now();
      } else if (document.visibilityState === 'visible' && hasBeenHidden.current) {
        const inactiveTime = Date.now() - lastActiveTime.current;
        console.log(`Tab was inactive for ${inactiveTime}ms`);
        
        // If the tab was inactive for more than 2 seconds, force a reload
        // This is the most reliable way to handle session recovery in older tabs
        if (inactiveTime > 2000) {
          console.log('Tab was inactive for a significant time, reloading page');
          window.location.reload();
          return;
        }
        
        // For shorter inactive periods, try session recovery
        recoverSession();
      }
    };

    // Core session recovery function
    const recoverSession = async () => {
      if (isRecovering) return;
      
      setIsRecovering(true);
      console.log('Attempting session recovery');
      
      try {
        const { data } = await supabase.auth.getSession();
        
        if (!data.session) {
          console.log('No session found');
          setIsRecovering(false);
          return;
        }
        
        // Test if session is valid
        try {
          const { error } = await supabase.from('user_profiles').select('count').limit(1);
          
          if (error) {
            console.log('Session test failed:', error.message);
            
            if (error.message.includes('JWT expired') || 
                error.message.includes('Invalid Refresh Token') ||
                error.message.includes('invalid token')) {
              console.log('Token expired, refreshing session');
              await supabase.auth.refreshSession();
            }
          }
        } catch (error) {
          console.error('Error testing session:', error);
          // Force refresh token on any error
          await supabase.auth.refreshSession();
        }
      } catch (error) {
        console.error('Session recovery error:', error);
      } finally {
        setIsRecovering(false);
      }
    };
    
    // Handle storage events from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      const authTokenKey = 'sb-otfbpcicwhfqgupzvoyx-auth-token'; // Confirm this is your auth token key
      
      if (event.key === authTokenKey) {
        console.log('Auth token changed in another tab');
        // Force reload on any auth token changes
        window.location.reload();
      }
    };
    
    // Set up all event listeners
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', recoverSession);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('online', recoverSession);
    
    // Initial session check
    recoverSession();
    
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', recoverSession);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('online', recoverSession);
    };
  }, [isRecovering]);
  
  return null;
}